'use client'

import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import {routeFactory} from '@/routing/route-factory';
import HeaderNavLink from './HeaderNavLink';
import NavigationDrawer from './NavigationDrawer';
import {
  StyledDesktopNav,
  StyledHeaderAppBar,
  StyledHeaderContainer,
  StyledHeaderToolbar,
  StyledLogoLink,
  StyledMobileMenuButton,
} from '@/components/header/Header.styles';
import {HeaderLinkFieldsFragment} from '@generated/graphql';
import {StyledLanguageSwitcherContainer} from '@/components/language-switcher/LanguageSwitcher.styles';
import {LanguageSwitcher} from '@/components/language-switcher/LanguageSwitcher';
import {IconButton, Typography} from '@mui/material';
import {PersonIcon} from '@/components/ui/icons/PersonIcon';
import Image from 'next/image';
import {hasLinkAndLabel} from '@/lib/type-guards';

const adminUrl = process.env.NEXT_PUBLIC_SPOTLIGHT_ADMIN_URL;

type HeaderClientProps = {
    links: HeaderLinkFieldsFragment[];
    heading?: string | null;
};

export type LinkWithLink = HeaderLinkFieldsFragment & {
    link: string;
    label: string
}

export default function Header({ links, heading }: HeaderClientProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollYRef = useRef(0);

  useEffect(() => {
    const navBarBackgroundThresholdPx = 24;

    let rafId: number | null = null;

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const lastY = lastScrollYRef.current;

        const isScrollingDown = currentY > lastY;
        lastScrollYRef.current = currentY;

        setIsScrolled(currentY > navBarBackgroundThresholdPx);

        const beyondOneViewport = currentY > window.innerHeight;
        setIsHeaderVisible(!(isScrollingDown && beyondOneViewport));
        rafId = null;
      });
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  const safeLinks = links.filter(hasLinkAndLabel).map(link => ({ ...link, href: link.link! }));

  return (
    <>
      <StyledHeaderAppBar position='fixed' elevation={0} isScrolled={isScrolled} isHeaderVisible={isHeaderVisible}>
        <StyledHeaderContainer maxWidth='xl'>
          <StyledHeaderToolbar disableGutters>
            <StyledLogoLink href={routeFactory.home.root()}>
              <Image src='/tv4-logo.svg' alt='TV4 Commercial' height={36} width={36} />
              {heading && (
                <Typography variant='h4' style={{  whiteSpace: 'nowrap'}}>
                  {heading}
                </Typography>
              )}
            </StyledLogoLink>

            <StyledDesktopNav>
              {safeLinks.map((link) => (
                <HeaderNavLink key={link.label} link={link} onClose={() => setDrawerOpen(false)} />
              ))}
            </StyledDesktopNav>

            <StyledLanguageSwitcherContainer>
              {adminUrl && (
                <IconButton component='a' href={adminUrl} target='_blank' rel='noopener noreferrer' color='inherit' size='small' aria-label='Admin portal'>
                  <PersonIcon />
                </IconButton>
              )}
              <LanguageSwitcher flagSize={24} />
            </StyledLanguageSwitcherContainer>
            <StyledMobileMenuButton
              color='inherit'
              onClick={() => setDrawerOpen(true)}
              aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={drawerOpen}
              aria-controls='primary-nav-drawer'
            >
              <MenuIcon />
            </StyledMobileMenuButton>
          </StyledHeaderToolbar>
        </StyledHeaderContainer>
      </StyledHeaderAppBar>
      <NavigationDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} links={safeLinks} />
    </>
  );
}